import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next' // LAP-733 (GE022)
import { makeGetRequest } from 'utils/api'
import ComboBox from 'Components/ComboBox'

function AccountInfoStep({
  accountInfo,
  setAccountInfo,
  setError,
  onNext,
}) {
  const { t } = useTranslation('layout') // LAP-733 (GE022)
  const [tmpBank, setTempBank] = useState([])
  const [tmpAccType, setTmpAccType] = useState([])
  const [tmpDevise, setTmpDevise] = useState([])

  const fetchBankData = async () => {
    try {
      const { data } = await makeGetRequest('/api/Finance/Banks/', {}, true)
      const filterBankData = []
      data.map((v) => {
        filterBankData.push({ label: v.Nom + ' - ' + v.Ville, value: v.id })
        return ''
      })
      setTempBank([...filterBankData])
    } catch (error) {
      console.error('Error fetching banks:', error)
      setError(t('mesComptes.import.errors.loadBanks')) // LAP-733 (GE022)
    }
  }

  const fetchAccType = async () => {
    try {
      const filterAccType = []
      const { data } = await makeGetRequest('/api/Finance/AccounType/', {}, true)
      data.map((v) => {
        filterAccType.push({ label: v.TypeCompte, value: v.id })
        return ''
      })
      setTmpAccType([...filterAccType])
    } catch (error) {
      console.error('Error fetching account types:', error)
      setError(t('mesComptes.import.errors.loadAccountTypes')) // LAP-733 (GE022)
    }
  }

  const fetchDevise = async () => {
    try {
      const filterDevise = []
      const { data } = await makeGetRequest('/api/Finance/devise-list/', {}, true)
      data.map((v) => {
        // Backend stores the currency code string (e.g. "CHF"), not an id,
        // so the option value is the code itself.
        const code = v.Devise || v.Code_iso
        if (!code) return ''
        const label = v.Designation ? `${code} - ${v.Designation}` : code
        filterDevise.push({ label, value: code })
        return ''
      })
      setTmpDevise([...filterDevise])
    } catch (error) {
      console.error('Error fetching currencies:', error)
      setError(t('mesComptes.import.errors.loadDevises')) // LAP-733 (GE022)
    }
  }

  useEffect(() => {
    const loadData = async () => {
      await fetchBankData()
      await fetchAccType()
      await fetchDevise()
    }
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setError])

  const handleAutocomplete = (name, val) => {
    const extractLabel = val
    setAccountInfo({ ...accountInfo, [name]: extractLabel && extractLabel.value })
  }

  const handleChange = (e) => {
    setAccountInfo({ ...accountInfo, [e.target.name]: e.target.value })
  }

  const handleValidateAndNext = () => {
    // Validate all required fields
    if (!accountInfo.bankData) {
      setError(t('mesComptes.import.errors.selectBank')) // LAP-733 (GE022)
      return
    }
    if (!accountInfo.accountType) {
      setError(t('mesComptes.import.errors.selectAccountType')) // LAP-733 (GE022)
      return
    }
    if (!accountInfo.IBAN) {
      setError(t('mesComptes.import.errors.enterIBAN')) // LAP-733 (GE022)
      return
    }
    if (!accountInfo.NumeroCompte) {
      setError(t('mesComptes.import.errors.enterNumeroCompte')) // LAP-733 (GE022)
      return
    }
    if (!accountInfo.Devise) {
      setError(t('mesComptes.import.errors.enterDevise')) // LAP-733 (GE022)
      return
    }
    if (!accountInfo.DateOuverture) {
      setError(t('mesComptes.import.errors.enterDateOuverture')) // LAP-733 (GE022)
      return
    }

    setError(null)
    onNext()
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('mesComptes.import.step1.title') /* LAP-733 (GE022) */}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('mesComptes.import.step1.description') /* LAP-733 (GE022) */}
      </Typography>

      <Grid container spacing={2}>
        {/* Etablissement Financier */}
        <Grid item xs={12}>
          <ComboBox
            handleAutoComplete={handleAutocomplete}
            label={t('mesComptes.import.step1.bankLabel') /* LAP-733 (GE022) */}
            data={tmpBank}
            name="bankData"
          />
        </Grid>

        {/* Type De Compte */}
        <Grid item xs={12}>
          <ComboBox
            handleAutoComplete={handleAutocomplete}
            data={tmpAccType}
            name="accountType"
            label={t('mesComptes.import.step1.accountTypeLabel') /* LAP-733 (GE022) */}
          />
        </Grid>

        {/* IBAN */}
        <Grid item xs={12}>
          <TextField
            label={t('mesComptes.import.step1.ibanLabel') /* LAP-733 (GE022) */}
            name="IBAN"
            fullWidth
            onChange={handleChange}
            value={accountInfo.IBAN || ''}
            variant="outlined"
            size="small"
            placeholder={t('mesComptes.import.step1.ibanPlaceholder') /* LAP-733 (GE022) */}
          />
        </Grid>

        {/* Numero Compte */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t('mesComptes.import.step1.numeroCompteLabel') /* LAP-733 (GE022) */}
            name="NumeroCompte"
            fullWidth
            onChange={handleChange}
            value={accountInfo.NumeroCompte || ''}
            variant="outlined"
            size="small"
            placeholder={t('mesComptes.import.step1.numeroComptePlaceholder') /* LAP-733 (GE022) */}
          />
        </Grid>

        {/* Devise */}
        <Grid item xs={12} md={6}>
          <ComboBox
            handleAutoComplete={handleAutocomplete}
            data={tmpDevise}
            name="Devise"
            label={t('mesComptes.import.step1.deviseLabel') /* LAP-733 (GE022) */}
          />
        </Grid>

        {/* Date Ouverture */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t('mesComptes.import.step1.dateOuvertureLabel') /* LAP-733 (GE022) */}
            name="DateOuverture"
            fullWidth
            onChange={handleChange}
            value={accountInfo.DateOuverture || ''}
            variant="outlined"
            size="small"
            type="date"
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Numero Client */}
        <Grid item xs={12} md={6}>
          <TextField
            label={t('mesComptes.import.step1.numeroClientLabel') /* LAP-733 (GE022) */}
            name="NumeroClient"
            fullWidth
            onChange={handleChange}
            value={accountInfo.NumeroClient || ''}
            variant="outlined"
            size="small"
            placeholder={t('mesComptes.import.step1.numeroClientPlaceholder') /* LAP-733 (GE022) */}
          />
        </Grid>

        {/* Clé RiB */}
        <Grid item xs={12}>
          <TextField
            label={t('mesComptes.import.step1.cleRibLabel') /* LAP-733 (GE022) */}
            name="CléRiB"
            fullWidth
            onChange={handleChange}
            value={accountInfo.CléRiB || ''}
            variant="outlined"
            size="small"
            placeholder={t('mesComptes.import.step1.cleRibPlaceholder') /* LAP-733 (GE022) */}
          />
        </Grid>
      </Grid>

      {/* Navigation */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="contained" onClick={handleValidateAndNext}>
          {t('mesComptes.import.step1.nextButton') /* LAP-733 (GE022) */}
        </Button>
      </Box>
    </Box>
  )
}

export default AccountInfoStep
